/* eslint-disable camelcase */

import classNames from 'classnames';
import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { isServerSide } from 'helpers/ssr';
import { Badge } from 'palette/Badge';
import { ConfigContext } from 'palette/Config';
import { Icon } from 'palette/Icon';
import { Svg } from 'palette/Svg';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslate } from '@common/Translations';
import { isFeatureActive } from '@helpers/index';

import '../styles/SlidePanelRow.scss';

const getBackgroundImageName = (data) => {
    const match = data.match(/bg-img-([\w\S]+)/);

    if (match) {
        return match[1];
    }

    return null;
};

/**
 * Props:
 * theme - default|mod1|mod2
*/
function SlidePanelRow({
    badge,
    hasArrow = false,
    customAttributes = {},
    leftContent,
    rightContent,
    rightIcon,
    label,
    count,
    countPosition,
    onClick,
    iconLeft,
    color,
    colorPosition,
    isActive,
    isUsingRouter,
    URL,
    hint,
    customClass,
    content,
    theme,
    isNew,
    imagesSourceUrl,
    resource,
    value = '',
    highlight,
    isExternal
}) {
    const translate = useTranslate();
    const config = useConfig();
    const { svgPath } = useContext(ConfigContext);
    const dynamicAttributes = {};
    const styles = {};
    const icon = !isServerSide() && App.Config?.Listing?.filterIcons && App.Config?.Listing?.filterIcons[value];
    let hasImage = false;
    let WrapperTag = 'div';
    const isMobileNavRefresh = isFeatureActive('PLT-34079_mobile_nav_refresh_2023', config);

    if (URL && theme !== 'divider') {
        dynamicAttributes.href = URL;
        WrapperTag = 'a';
    }

    if (isUsingRouter) {
        dynamicAttributes.to = URL;
        WrapperTag = Link;
    }

    if (isExternal) {
        dynamicAttributes.target = '_blank';
    }

    if (theme === 'mod2') {
        const backgroundImageName = getBackgroundImageName(customClass);

        if (backgroundImageName) {
            styles.backgroundImage = `url(${[imagesSourceUrl, '/', backgroundImageName].join('')}.png)`;
            hasImage = true;
        }
    }

    if (['mod3', 'mod6', 'mod7', 'mod10', 'mod11', 'mod12'].indexOf(theme) > -1 && resource) {
        const { category_image } = resource;

        if (category_image) {
            styles.backgroundImage = `url(${category_image.url})`;
            hasImage = true;
        }
    }

    return (
        <WrapperTag
            {...dynamicAttributes}
            {...customAttributes}
            className={classNames('slide-panel-row', {
                'slide-panel-row--active': isActive,
                'slide-panel-row--content': !!content,
                'slide-panel-row--image': hasImage,
                [`slide-panel-row--${theme}`]: theme
            }, customClass)}
            style={styles}
            onClick={theme === 'divider' ? null : onClick}
        >
            <div className="slide-panel-row__first-row">
                {leftContent && <div className="slide-panel-row__left-content">{leftContent}</div>}
                {color && colorPosition === 'left' && <span className={`slide-panel-row__color2 colorcode-${color}`} />}
                {typeof iconLeft !== 'undefined' && (
                    <Icon
                        className="slide-panel-row__icon-left"
                        name={iconLeft}
                    />
                )}
                <span className={classNames('slide-panel-row__label', { 'slide-panel-row__label--mod13': isMobileNavRefresh })}>
                    {label}
                    {count > 0 && countPosition === 'label' && (
                        <div className="slide-panel-row__count2">
                            (
                            {count}
                            )
                        </div>
                    )}
                    {hint && <div>{hint}</div>}
                    {isNew && <span className="navigation_new-tag">{translate('navigation.new_tag')}</span>}
                    {icon && (
                        <Svg
                            className="slide-panel-row__icon"
                            name={icon}
                            svgUrl={svgPath}
                        />
                    )}
                </span>
                {
                    highlight
                    && (
                        <div className="slide-panel-row__highlight-wrapper">
                            <span
                                className="slide-panel-row__highlight-item"
                                style={{
                                    backgroundColor: highlight.backgroundColor,
                                    color: highlight.textColor
                                }}
                            >
                                {highlight.text}
                            </span>
                        </div>
                    )
                }
                {badge && (
                    <Badge
                        className="ml-8"
                        label={badge.value}
                        size="xs"
                        theme={badge.theme}
                    />
                )}
                {count > 0 && !countPosition && <span className="slide-panel-row__count">{count}</span>}
                {hasArrow
                    && (
                        <Icon
                            className="slide-panel-row__icon-right"
                            color="black"
                            name={rightIcon || 'palette_chevron_right'}
                            size={isMobileNavRefresh ? 'x-small' : 'medium'}
                        />
                    )}
                {color && !colorPosition && <span className={`slide-panel-row__color colorcode-${color}`} />}
                {rightContent && <div className="slide-panel-row__right-content">{rightContent}</div>}
            </div>
            {typeof content !== 'undefined' && <div className="slide-panel-row__second-row">{content}</div>}
        </WrapperTag>
    );
}

export default SlidePanelRow;
